import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { useFormContext } from 'react-hook-form';

import type {
  ICargoType,
  ILine,
  ILineDescriptor,
  IModality,
  IServiceType,
} from '@quick/go-api-types/services/country/lines/[id]';
import type { IClientInfo, IClientList } from '@quick/go-api-types/users';

import type { IForm } from '@/components/demandForm';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { useFetch } from '@/layers/core/deal/services/useFetch';

const Context = createContext<
  {
    user?: IClientInfo;
    line?: ILine;
    serviceType?: IServiceType;
    cargoType?: ICargoType;
    modality?: IModality;
    country_id?: number;
  } & Partial<ReturnType<typeof useFetch<ILineDescriptor>>>
>({});

export const FormDataContext = ({ children }: PropsWithChildren) => {
  const { watch, setValue } = useFormContext<IForm>();

  // Get selected user data
  const user_id = watch('user_id');
  const { data: usersData } = useFetch<IClientList>(
    !!user_id && {
      url: '/users',
      auth: true,
      params: { user_id },
      allowNoAuth: true,
    }
  );
  const user = usersData?.data?.at(0);

  const { id: countryId } = useCountry();

  const country_id = user?.country_id ?? countryId;

  useEffect(() => {
    setValue('country_id', country_id);
  }, [country_id, setValue]);

  const swr = useFetch<ILineDescriptor>(
    {
      url: `/services/country/lines/${country_id}`,
      auth: true,
      params: { user_id },
      allowNoAuth: true,
    },
    {
      shouldRetryOnError(err) {
        // TODO: Remove when back fixes this
        return err.response?.status !== 403;
      },
      // keepPreviousData: true,
    }
  );

  const { data } = swr;

  const lineId = watch('line_id');
  const serviceTypeId = watch('service_type_id');
  const loadTypeId = watch('cargo_type_id');
  const modalityId = watch('modality_id');
  const line = useMemo(
    () => data?.lines?.find(({ id }) => `${id}` === `${lineId}`),
    [data, lineId]
  );

  const serviceType = useMemo(
    () => line?.service_types.find(({ id }) => `${id}` === `${serviceTypeId}`),
    [line, serviceTypeId]
  );

  const cargoType = useMemo(
    () =>
      serviceType?.cargo_types?.find(
        ({ id }) => id?.toString() === loadTypeId?.toString()
      ),
    [serviceType, loadTypeId]
  );

  const modality = useMemo(
    () => serviceType?.modalities.find(({ id }) => `${id}` === `${modalityId}`),
    [serviceType, modalityId]
  );

  return (
    <Context.Provider
      value={{
        user,
        line,
        serviceType,
        modality,
        cargoType,
        country_id,
        ...swr,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useFormDataContext = () => useContext(Context);
